/** @jsxImportSource theme-ui */
import { useBreakpointIndex } from '@theme-ui/match-media';
import { parseISO } from 'date-fns';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Card, Divider, Grid } from 'semantic-ui-react';
import Shiitake from 'shiitake';

import { AppStateContext } from 'api/AppStateContext';
import { ApiDateType } from 'client';
import * as dto from 'client/dto';
import { BdScrubberModel } from 'models/BdScrubberModel';
import { AggregationCat } from 'models/DiseaseModels';
import { AggregationThresholdsDetails } from 'utils/constants';
import { sxtheme } from 'utils/cssHelpers';
import {
  formatDateISO,
  formatDateOrRangeShort,
  formatDateUntilToday,
  getCurrentUtcDate,
  getDatePartStr,
  getMomentAgo,
  parseIsoDateStartOfDayUtc0,
} from 'utils/dateTimeHelpers';
import { ComputeAggregationCategory, MapAggregationCat2AggregationDays } from 'utils/modelHelpers';
import { isMobile } from 'utils/responsive';
import { formatNumber } from 'utils/stringFormattingHelpers';

import { BdIcon } from 'components/_common/BdIcon';
import { IClickable } from 'components/_common/common-props';
import { FlexGroup } from 'components/_common/FlexGroup';
import { BdCardFooterLink } from 'components/_common/SemanticAddons/BdCardFooterLink';
import { Typography } from 'components/_common/Typography';
import { BdScrubber } from 'components/_controls/BdScrubber';
import {
  DiseaseActivityDatumType,
  DiseaseActivityDatumUpdate,
} from 'components/_graphs/_common/DiseaseActivityModels';
import { BdDiseaseActivityGraph } from 'components/_graphs/BdDiseaseActivityGraph/BdDiseaseActivityGraph';

import { DaGraphLegend } from '../../../diseaseActivityPanels/components/DiseaseActivityCards/DaGraphLegend';
import { IconButton } from '../IconButton';

// =====================================================================================================================================
const GraphWidth = 275;
const GraphPaddingR = 25;
const GraphMarginPopoutHackPx = 15;
const GraphMarginChipsAlignYAxisCutoffHackPx = 7;
const DateToday = getCurrentUtcDate();
const Date30DaysAgo = getMomentAgo('last-30-days').toDate();
const Date30DaysInMilleseconds = 1000 * 60 * 60 * 24 * 30;
// =====================================================================================================================================

export type GetDiseaseActivityApiFunction = (
  startDate: Date,
  endDate: Date,
  aggregationCategory: AggregationCat
) => Promise<dto.TemporalActivityModel[]>;

type DiseaseActivityCardProps = {
  isSelected: boolean;
  isGlobal?: boolean;
  countryName?: string;
  firstActivityUpdateDateStr: ApiDateType;
  caseCounts: dto.CaseCountModel;
  onNeedApiGetDiseaseActivity: GetDiseaseActivityApiFunction;
  onGoToActivityHistory: (activityDate?: string) => void;
};

/** @deprecated */
export const DiseaseActivityCard: React.FC<DiseaseActivityCardProps> = ({
  isSelected,
  isGlobal = false,
  countryName,
  firstActivityUpdateDateStr,
  caseCounts,
  onNeedApiGetDiseaseActivity,
  onGoToActivityHistory,
}) => {
  const { appState } = useContext(AppStateContext);
  const temporalWindowStartDate = useMemo(() => parseISO(appState.startDate), [appState.startDate]);
  const temporalWindowEndDate = useMemo(() => new Date(), []);
  const isMobileDevice = isMobile(useBreakpointIndex());

  const [isLoading, setisLoading] = useState(true);
  const [graphData, setGraphData] = useState<DiseaseActivityDatumType[]>(null);
  const [showAverageCurveInLegend, setShowAverageCurveInLegend] = useState(false);

  const scrubberMinValue = useMemo(
    () =>
      firstActivityUpdateDateStr
        ? moment
            .min(
              moment(parseIsoDateStartOfDayUtc0(firstActivityUpdateDateStr)),
              moment(Date30DaysAgo)
            )
            .toDate()
        : Date30DaysAgo,
    [firstActivityUpdateDateStr]
  );

  const scrubberMaxValue = useMemo(() => DateToday, []);

  const [selectedScrubberValue, setSelectedScrubberValue] = useState<BdScrubberModel>({
    startValue: scrubberMinValue.getTime(),
    endValue: scrubberMaxValue.getTime(),
  });

  const [releasedScrubberValue, setReleasedScrubberValue] = useState<BdScrubberModel>({
    startValue: scrubberMinValue.getTime(),
    endValue: scrubberMaxValue.getTime(),
  });

  const [aggregationCategoryState, setAggregationCategoryState] = useState<AggregationCat>('day');

  const aggregationCategory = useMemo(
    () =>
      ComputeAggregationCategory({
        startDate: new Date(releasedScrubberValue.startValue),
        endDate: new Date(releasedScrubberValue.endValue),
        thresholds: AggregationThresholdsDetails,
      }),
    [releasedScrubberValue.startValue, releasedScrubberValue.endValue]
  );

  const scrubberSetup = {
    min: scrubberMinValue.getTime(),
    max: scrubberMaxValue.getTime(),
    tipFormatter: val => moment(new Date(val)).format('MMM DD, YYYY'),
    disabled: scrubberMinValue.getTime() >= Date30DaysAgo.getTime(),
    minLabel: moment(scrubberMinValue).format('MMM YYYY').toUpperCase(),
    maxLabel: moment(scrubberMaxValue).format('MMM YYYY').toUpperCase(),
  };

  const endGraphDateValue = new Date(selectedScrubberValue.endValue);
  endGraphDateValue.setDate(endGraphDateValue.getDate() - 1);

  const diseaseActivityCall = () => {
    onNeedApiGetDiseaseActivity(
      new Date(releasedScrubberValue.startValue),
      new Date(releasedScrubberValue.endValue),
      aggregationCategory
    )
      .then(data => {
        const newGraphData = (data || []).map(
          x =>
            ({
              date: parseIsoDateStartOfDayUtc0(x.startDate),
              dateEnd: parseIsoDateStartOfDayUtc0(x.endDate),
              series1: x.caseCounts?.reportedCases || 0,
              series2: x.caseCounts?.deaths || 0,
              updatesCount: x.totalDescriptionCount, // NOTE: to save response, BE gives only 2 descriptions for preview (see TemporalActivityRequestModel.descriptionCountForAggregationTime: 2)
              updates: x.descriptions?.map(
                d =>
                  ({
                    date: d.reportDate,
                    locationName: d.locationName,
                    text: d.description,
                    isRawHtml: d.hasHtmlTagsInDescription,
                  } as DiseaseActivityDatumUpdate)
              ),
            } as DiseaseActivityDatumType)
        );
        setGraphData(newGraphData);
        setAggregationCategoryState(aggregationCategory);
      })
      .finally(() => {
        setisLoading(false);
      });
  };

  useEffect(() => {
    diseaseActivityCall();
  }, [releasedScrubberValue, aggregationCategory]);

  const labelCases = isGlobal ? 'Global reported cases' : 'Reported cases';
  const labelDeaths = isGlobal ? 'Global reported deaths' : 'Reported deaths';
  const labelFooterLink = isGlobal
    ? 'See all global activity'
    : `See all activity in ${countryName}`;
  const labelCardTitle = isGlobal ? 'Global activity' : `Activity in ${countryName}`;

  const renderGraphPopupLower = useCallback(
    (datum: DiseaseActivityDatumType, isActive: boolean, callbackClosePopup: () => void) => (
      <div>
        <div sx={{ position: 'relative' }}>
          <Typography variant="subtitle2" color="white" marginBottom="12px">
            {formatDateOrRangeShort(datum.date, datum.dateEnd)}
          </Typography>
          {isActive && (
            <IconButton
              size="medium"
              ariaLabel="Close popup"
              icon="icon-close"
              color="white"
              onClick={() => callbackClosePopup()}
              sx={{ position: 'absolute', top: 0, right: 0 }}
            />
          )}
        </div>
        <Grid columns={2} divided className="dark-bg">
          <Grid.Row>
            <Grid.Column>
              <Typography variant="caption" color="stone50" nowrap>
                {labelCases}
              </Typography>
              <Typography
                variant="subtitle2"
                color="white"
                marginBottom="0"
                nowrap
                sx={{ opacity: 0.93 }}
              >
                {formatNumber(!datum.isGhost && datum.series1, null, null, 'emdash-0')}
                {datum.series1 < 0 && (
                  <BdIcon
                    name="icon-asterisk"
                    color="deepSea40"
                    bold
                    sx={{ verticalAlign: 'middle' }}
                  />
                )}
              </Typography>
            </Grid.Column>
            <Grid.Column>
              <Typography variant="caption" color="stone50" nowrap>
                {labelDeaths}
              </Typography>
              <Typography
                variant="subtitle2"
                color="white"
                marginBottom="0"
                nowrap
                sx={{ opacity: 0.93 }}
              >
                {formatNumber(!datum.isGhost && datum.series2, null, null, 'emdash-0')}
                {datum.series2 < 0 && (
                  <BdIcon
                    name="icon-asterisk"
                    color="deepSea40"
                    bold
                    sx={{ verticalAlign: 'middle' }}
                  />
                )}
              </Typography>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        {datum.updatesCount > 0 && datum.updates?.length > 0 && !isActive && (
          <>
            <Divider sx={{ '&.ui.divider': { mt: '16px', mb: 0 } }} />
            <div sx={{ position: 'relative' }}>
              <PopupUpdateSection update={datum.updates[0]} dontShowLink />
              <div
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '20px',
                  bottom: 0,
                  background: 'linear-gradient(180deg, rgba(37, 40, 56, 0) 0%, #252838 100%);',
                }}
              />
            </div>
          </>
        )}
        {datum.updatesCount > 0 && isActive && (
          <>
            <Divider sx={{ '&.ui.divider': { my: '16px' } }} />
            {datum.updates.map((update, i) => (
              <PopupUpdateSection
                key={`popup-update${i}`}
                update={update}
                onClick={() => {
                  onGoToActivityHistory(getDatePartStr(update.date));
                  callbackClosePopup();
                }}
              />
            ))}
            {datum.updatesCount > 2 && (
              <Typography
                variant="body2"
                color="white"
                sx={{ cursor: 'pointer', textDecoration: 'underline', mt: '10px' }}
                onClick={() => {
                  onGoToActivityHistory(getDatePartStr(datum.updates[0].date));
                  callbackClosePopup();
                }}
              >
                See {datum.updatesCount} situational updates
              </Typography>
            )}
          </>
        )}
        {(datum.series1 < 0 || datum.series2 < 0) && (
          <div sx={{ mt: '16px' }}>
            <Typography variant="caption" color="stone10">
              <BdIcon
                name="icon-asterisk"
                color="deepSea40"
                bold
                sx={{ verticalAlign: 'middle' }}
              />{' '}
              Negative values are likely due to corrections made to previous reported case and death
              counts
            </Typography>
          </div>
        )}
      </div>
    ),
    [labelCases, labelDeaths]
  );
  const renderGraphPopupUpper = useCallback(
    (datum: DiseaseActivityDatumType) => (
      <FlexGroup alignItems="flex-start" prefix={<BdIcon name="icon-information" color="white" />}>
        <Typography variant="caption2" color="white">
          {formatNumber(datum.updatesCount, 'update', 'updates', 'emdash-0')}
        </Typography>
        <Typography variant="label-small" color="white">
          Click bar to view
        </Typography>
      </FlexGroup>
    ),
    []
  );

  return (
    <Card
      fluid
      sx={{
        '&.ui.card': {
          border: sxtheme(t => `1px solid ${t.colors.stone20}`),
        },
      }}
    >
      <Card.Content>
        <FlexGroup
          alignItems="center"
          // prefix={<BdIcon name="icon-calendar" color="stone50" />}
          suffix={
            <Typography variant="caption2" color="stone90">
              {formatDateUntilToday(temporalWindowStartDate)}
            </Typography>
          }
        >
          <Typography variant="h6" color="stone90">
            {labelCardTitle}
          </Typography>
        </FlexGroup>
      </Card.Content>
      <Card.Content>
        <Grid columns={2} divided="vertically" sx={{ mb: '-12px !important' }}>
          <Grid.Row divided>
            <Grid.Column>
              <Typography variant="caption" color="stone70">
                <span sx={{ verticalAlign: 'middle', mr: '2px' }}>{labelCases}</span>
                {/* TODO: not sure if james will bring these back, keeping verticalAlign for now */}
                {/* <BdIcon name="icon-information" sx={{ verticalAlign: 'middle' }} /> */}
              </Typography>
              <Typography variant="subtitle2" color="stone90">
                {formatNumber(!!caseCounts && caseCounts.reportedCases, null, null, 'emdash-0')}
              </Typography>
            </Grid.Column>
            <Grid.Column>
              <Typography variant="caption" color="stone70">
                <span sx={{ verticalAlign: 'middle', mr: '2px' }}>{labelDeaths}</span>
                {/* <BdIcon name="icon-information" sx={{ verticalAlign: 'middle' }} /> */}
              </Typography>
              <Typography variant="subtitle2" color="stone90">
                {formatNumber(!!caseCounts && caseCounts.deaths, null, null, 'emdash-0')}
              </Typography>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Card.Content>
      <Card.Content>
        <div
          sx={{
            display: 'flex',
            flexDirection: 'column-reverse', // CODE: 6b218f23
          }}
        >
          <div
            sx={{
              mx: `-${GraphMarginPopoutHackPx}px`,
              mt: `-${GraphMarginChipsAlignYAxisCutoffHackPx}px`, // CODE: 6b218f23: column-reverse due to z-indexing from negative margin axis hack
            }}
          >
            <BdDiseaseActivityGraph
              graphData={graphData}
              minDate={new Date(selectedScrubberValue.startValue)}
              maxDate={new Date(selectedScrubberValue.endValue)}
              highlightStartDate={temporalWindowStartDate}
              highlightEndDate={temporalWindowEndDate}
              aggregationDays={MapAggregationCat2AggregationDays[aggregationCategoryState]}
              isLoading={isLoading}
              onShowingAverageChange={setShowAverageCurveInLegend}
              renderPopupLower={renderGraphPopupLower}
              renderPopupUpper={renderGraphPopupUpper}
              disablePopupUpper={isMobileDevice}
              onlyWithUpdatesMakeActiveOnClick={!isMobileDevice}
              width={GraphWidth + 2 * GraphMarginPopoutHackPx}
              height={120 + GraphMarginChipsAlignYAxisCutoffHackPx}
              padding={{
                top: GraphMarginChipsAlignYAxisCutoffHackPx,
                left: GraphMarginPopoutHackPx,
                bottom: 16,
                right: GraphPaddingR + GraphMarginPopoutHackPx,
              }}
            />
          </div>
          <div sx={{ mt: '-8px', mb: '10px' }}>
            <DaGraphLegend
              aggregationCategory={aggregationCategoryState}
              showAvg={showAverageCurveInLegend}
            />
          </div>
        </div>
        <div
          sx={{
            width: `${GraphWidth - GraphPaddingR}px`,
            mt: '15px',
            mb: '9px',
          }}
        >
          <BdScrubber
            disabled={scrubberSetup.disabled}
            min={scrubberSetup.min}
            max={scrubberSetup.max}
            minLabel={scrubberSetup.minLabel}
            maxLabel={scrubberSetup.maxLabel}
            defaultValue={selectedScrubberValue}
            onValueChange={setSelectedScrubberValue}
            releaseTrigger={setReleasedScrubberValue}
            tipFormatter={scrubberSetup.tipFormatter}
            minInterval={Date30DaysInMilleseconds}
          />
        </div>
        {/* <pre>{JSON.stringify({aggregationCategory, agrDays: MapTemporalCaseCountAggregationCategory2AggregationDays[aggregationCategory]}, null, 2)}</pre> */}
      </Card.Content>
      <BdCardFooterLink
        onClick={() => onGoToActivityHistory()}
        label={labelFooterLink}
        isSelected={isSelected}
      />
    </Card>
  );
};

// =====================================================================================================================================

type PopupUpdateSectionProps = IClickable & {
  update: DiseaseActivityDatumUpdate;
  dontShowLink?: boolean;
};

export const PopupUpdateSection: React.FC<PopupUpdateSectionProps> = ({
  onClick,
  update,
  dontShowLink = false,
}) => {
  return (
    <div sx={{ mt: '16px' }}>
      <Typography variant="overline" color="stone50">
        {formatDateISO(update.date)} | {update.locationName}
      </Typography>
      <Typography variant="body2" color="white">
        <FlexGroup alignItems="flex-end">
          {update.isRawHtml ? (
            <div dangerouslySetInnerHTML={{ __html: update.text }} />
          ) : (
            <Shiitake lines={2} throttleRate={200} className="shiitake">
              {update.text}
            </Shiitake>
          )}
          {!dontShowLink && (
            <div sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={onClick}>
              Read more
            </div>
          )}
        </FlexGroup>
      </Typography>
    </div>
  );
};
